import type { Brand, FlyerDocument, TextStyleName } from "./types";

// Q3: las FAMILIAS tipográficas ahora son DINÁMICAS por documento (se resuelven
// desde `brand.fonts` contra una allowlist en `./fonts.ts` y se proveen por
// contexto -- ver `FlyerFontProvider`/`useFlyerFonts`). Este módulo conserva
// SOLO los TAMAÑOS/pesos/interlineado (estáticos, espejados en
// `pipeline/flyer_metrics.py`); `fitFontSize` nunca usó la familia. Cada estilo
// declara su ROL (heading/body) y cada componente pinta la familia real del
// contexto según ese rol.
const FALLBACK_COLOR = "#000000";

// "$brand.primary" -> hex real del flyer. `brand` es opcional en el schema
// (a diferencia del storyboard del hermano, donde es obligatorio): un
// documento sin `brand` que igual referencia un token cae a un color de
// contraste seguro en vez de romper el render.
export const resolveToken = (
  brand: Brand | null | undefined,
  value: string,
): string => {
  if (!value.startsWith("$brand.")) {
    return value;
  }
  if (!brand) {
    return FALLBACK_COLOR;
  }
  const key = value.replace("$brand.", "") as keyof Brand["colors"];
  return brand.colors[key] ?? FALLBACK_COLOR;
};

export const resolveBrandColor = (
  flyer: FlyerDocument,
  value: string,
): string => resolveToken(flyer.brand, value);

// Rol tipográfico: qué familia del contexto (heading/body) pinta cada estilo.
export type FontRole = "heading" | "body";

interface TextStyleDef {
  fontSize: number; // px, referido a un canvas de 1080px de ancho
  role: FontRole;
  fontWeight: number;
  lineHeight: number;
}

// Tamaños base calibrados sobre un canvas de 1080px de ancho (mismo
// reference width que el storyboard del hermano). `scaledFontSize` los
// reescala al ancho real del formato del flyer (1080 en 1:1/4:5/9:16, 1920
// en 16:9) para que el texto guarde la misma proporción visual en los 4
// formatos en vez de verse diminuto en el más ancho.
export const textStyles: Record<TextStyleName, TextStyleDef> = {
  headline: {
    fontSize: 92,
    role: "heading",
    fontWeight: 800,
    lineHeight: 1.12,
  },
  subtitle: {
    fontSize: 58,
    role: "heading",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  body: {
    fontSize: 46,
    role: "body",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  price: {
    fontSize: 132,
    role: "heading",
    fontWeight: 800,
    lineHeight: 1.05,
  },
  cta: {
    fontSize: 50,
    role: "heading",
    fontWeight: 700,
    lineHeight: 1.15,
  },
  caption: {
    fontSize: 38,
    role: "body",
    fontWeight: 500,
    lineHeight: 1.25,
  },
};

const REFERENCE_WIDTH = 1080;

export const scaledFontSize = (base: number, canvasWidth: number): number =>
  base * (canvasWidth / REFERENCE_WIDTH);

// IM-1 (M2, docs/hueco-vocabulario.md #12): único lugar de todo el repo donde
// `TextLayer.size_factor` multiplica el tamaño base -- SIEMPRE ANTES de
// `fitFontSize`, nunca después ni en su lugar. TextLayer.tsx pasa por acá en
// vez de multiplicar inline para que no haya un segundo lugar (editor, un
// futuro caller) que lo aplique de nuevo o en el orden equivocado.
export const applySizeFactor = (
  baseFontSizePx: number,
  sizeFactor: number,
): number => baseFontSizePx * sizeFactor;

// M6/FIX2: reescritura conservadora + wrap-aware de `fitFontSize` -- causa
// raíz del bug de producción de colisión masiva de texto (16 capas de texto
// amontonadas). La versión anterior subestimaba el ancho de carácter
// (0.54 * fontSize) y contaba líneas dividiendo `content.length` a secas
// (ciego a los saltos de palabra reales), así que sobredimensionaba el
// tamaño de fuente que el director iba a necesitar.

// Ancho de carácter estimado como fracción del fontSize. Calibrado sobre el
// caso más ancho que usa el motor (headline/price en Poppins peso 800) para
// ser conservador en TODOS los estilos, ya que `fitFontSize` no recibe la
// familia/peso del texto (mismo contrato que antes: solo content/base/ancho).
const CHAR_WIDTH_FACTOR = 0.62;

// M2b (docs/plan-calidad-v3.md, AV-4): las mayúsculas no tienen x-height ni
// descendentes, así que un string en `text_transform=uppercase` ocupa más
// ancho que la mezcla normal de mayúsculas/minúsculas sobre la que está
// calibrado CHAR_WIDTH_FACTOR -- sin este factor, `estimateLines` subestima
// el ancho real de un texto uppercase y el layout-gate no detecta colisiones
// que sí van a ocurrir (el riesgo que el architect marcó para M2). Exportada:
// `pipeline/flyer_metrics.py` la espeja EXACTA (tests/test_flyer_metrics.py).
export const UPPERCASE_WIDTH_FACTOR = 1.18;

// Cuántas líneas se toleran al tamaño BASE antes de encoger -- un titular o
// precio corto de hasta 2 líneas se ve bien sin reducir; de la 3ra línea en
// adelante el bloque empieza a invadir el aire que necesitan las capas vecinas.
const MAX_LINES_AT_BASE_SIZE = 2;

// Al encoger, numerador MENOR que el "base*2" anterior (más agresivo) y piso
// MENOR (0.30 en vez de 0.35): un texto muy largo sigue reduciendo en vez de
// quedarse grande y desbordar.
const SHRINK_NUMERATOR_FACTOR = 1.6;
const SHRINK_FLOOR_FACTOR = 0.3;

// Estima cuántas líneas ocupa `content` a `fontSizePx` dentro de `maxWidthPx`
// simulando el wrap por PALABRAS -- una palabra larga que no entra en el
// ancho restante fuerza un salto de línea aunque el conteo total de
// caracteres "quepa" en promedio, cosa que `Math.ceil(length / maxChars)` (el
// estimador anterior) no podía capturar.
//
// M2b (AV-4): `letterSpacingEm` se SUMA al ancho por carácter -- el tracking
// CSS agrega un espacio fijo (en las mismas unidades de font-size) DESPUÉS de
// cada carácter, así que ensancha la línea igual que un CHAR_WIDTH_FACTOR más
// grande. El resultado se multiplica por UPPERCASE_WIDTH_FACTOR cuando el
// texto va a mayúsculas -- deliberadamente conservador: la combinación más
// ancha (tracking amplio + mayúsculas) es justo el caso que antes hacía
// volver colisiones sin avisar (ver el caso adversarial de
// tests/test_layout_gate.py).
// Factor de ancho por carácter (fracción del fontSize) que combina tracking
// (`letterSpacingEm`) y el ensanche de mayúsculas (`UPPERCASE_WIDTH_FACTOR`)
// -- factorizado para que `estimateLines` (líneas de un bloque que SÍ envuelve)
// y `estimateTextWidthPx` (ancho de una línea que NO envuelve, ver abajo)
// lean el mismo número en vez de que cada uno traiga su propia copia.
const charWidthFactorFor = (
  letterSpacingEm: number,
  uppercase: boolean,
): number =>
  (CHAR_WIDTH_FACTOR + letterSpacingEm) *
  (uppercase ? UPPERCASE_WIDTH_FACTOR : 1);

const estimateLines = (
  content: string,
  fontSizePx: number,
  maxWidthPx: number,
  letterSpacingEm = 0,
  uppercase = false,
): number => {
  const charWidthFactor = charWidthFactorFor(letterSpacingEm, uppercase);
  const maxCharsPerLine = Math.max(
    1,
    Math.floor(maxWidthPx / (fontSizePx * charWidthFactor)),
  );
  const words = content.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return 1;
  }
  let lines = 1;
  let currentLineLen = 0;
  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      // Palabra más larga que una línea entera: fuerza sus propios saltos.
      if (currentLineLen > 0) {
        lines += 1;
      }
      lines += Math.ceil(word.length / maxCharsPerLine) - 1;
      currentLineLen = word.length % maxCharsPerLine || maxCharsPerLine;
      continue;
    }
    const added =
      currentLineLen === 0 ? word.length : currentLineLen + 1 + word.length;
    if (added > maxCharsPerLine && currentLineLen > 0) {
      lines += 1;
      currentLineLen = word.length;
    } else {
      currentLineLen = added;
    }
  }
  return lines;
};

// M3 (docs/plan-calidad-v3.md): ancho estimado (px) de `content` en UNA sola
// línea (sin envolver) al tamaño final -- lo usa el editor
// (EditorVisualFlyer.tsx::medirCajaTexto) para dibujar la caja de selección
// de un TextLayer con `box`: a diferencia del ancho fijo `max_width` (sin
// caja), con `box` el wrapper es `fit-content` y abraza el texto real (ver
// TextLayer.tsx) -- el editor necesita el mismo ancho "desnudo" que ya
// calcula `estimateLines` en vez de reinventar el factor de carácter.
export const estimateTextWidthPx = (
  content: string,
  fontSizePx: number,
  letterSpacingEm = 0,
  uppercase = false,
): number =>
  content.length * fontSizePx * charWidthFactorFor(letterSpacingEm, uppercase);

// M6.2 (tuning de calibración: tras M6/FIX2 el tercio SUPERIOR del flyer
// seguía encimado en producción -- headline/land_size/price demasiado
// juntos y grandes). Causa raíz puntual: `MAX_LINES_AT_BASE_SIZE`/
// `SHRINK_*_FACTOR` de arriba son parejos para TODOS los estilos, así que un
// `price` (fontSize base 132, el más grande del tema) que envuelve a
// exactamente 2 líneas se queda al tamaño BASE sin encoger nada -- en el
// caso real de producción eso eran ~277px de alto (2 líneas x 132px x
// lineHeight 1.05) para un solo bloque, el mayor estorbo del tercio
// superior. `headline` y `price` son los dos estilos "dominantes" (los que
// más invaden el aire de sus vecinos cuando envuelven), así que reciben
// reglas MÁS agresivas que el resto del texto: encogen desde la 2da línea
// (no la 3ra, `maxLinesAtBase: 1`) y con un numerador menor (más agresivo
// para `price`, el mayor estorbo real). El piso (`shrinkFloorFactor`) se
// mantiene IGUAL o apenas por debajo del general -- un piso mucho más bajo
// (probado y descartado) sobre-encogía titulares largos hasta tamaños
// ilegibles, porque el estimador de líneas es conservador (ver
// `CHAR_WIDTH_FACTOR`) y un titular que "parece" necesitar 4-5 líneas al
// tamaño base en realidad entra en 1-2 al tamaño reducido. `subtitle`/
// `body`/`cta`/`caption` conservan el comportamiento general de arriba, sin
// cambios.
interface DominantStyleRule {
  maxLinesAtBase: number;
  shrinkNumeratorFactor: number;
  shrinkFloorFactor: number;
}

const DOMINANT_STYLE_RULES: Partial<Record<TextStyleName, DominantStyleRule>> =
  {
    headline: {
      maxLinesAtBase: 1,
      shrinkNumeratorFactor: 1.45,
      shrinkFloorFactor: 0.3,
    },
    price: {
      maxLinesAtBase: 1,
      shrinkNumeratorFactor: 1.3,
      shrinkFloorFactor: 0.28,
    },
  };

// Reduce el tamaño de fuente si el texto no cabe en ~MAX_LINES_AT_BASE_SIZE
// líneas del ancho dado (o en `maxLinesAtBase` de `DOMINANT_STYLE_RULES` si
// `style` es uno de los estilos dominantes). Espejo Python EXACTO en
// `pipeline/flyer_metrics.py` (`fit_font_size`/`estimate_lines`) -- lo usa
// `pipeline/layout_gate.py` para estimar el alto real de una capa de texto
// ANTES de renderizar; cualquier divergencia entre los dos es un bug (ver
// `tests/test_flyer_metrics.py`). `style` es opcional (compatible con
// llamadas anteriores a M6.2): sin él, el comportamiento es el general de
// siempre. M2b: `letterSpacingEm`/`uppercase` (defaults 0/false, mismo
// comportamiento EXACTO que antes de M2b cuando se omiten) se pasan a
// `estimateLines` para que la decisión de encoger conozca el ancho real que
// va a pintar el CSS (letter-spacing + text-transform), no solo el ancho
// "desnudo" del texto.
export const fitFontSize = (
  content: string,
  base: number,
  maxWidthPx: number,
  style?: TextStyleName,
  letterSpacingEm = 0,
  uppercase = false,
): number => {
  const rule = style ? DOMINANT_STYLE_RULES[style] : undefined;
  const maxLinesAtBase = rule?.maxLinesAtBase ?? MAX_LINES_AT_BASE_SIZE;
  const shrinkNumeratorFactor =
    rule?.shrinkNumeratorFactor ?? SHRINK_NUMERATOR_FACTOR;
  const shrinkFloorFactor = rule?.shrinkFloorFactor ?? SHRINK_FLOOR_FACTOR;

  const lines = estimateLines(
    content,
    base,
    maxWidthPx,
    letterSpacingEm,
    uppercase,
  );
  if (lines <= maxLinesAtBase) {
    return base;
  }
  const shrunk = Math.floor((base * shrinkNumeratorFactor) / lines);
  return Math.max(shrunk, base * shrinkFloorFactor);
};

// M2b (AV-1/AV-4, docs/plan-calidad-v3.md): alto total ESTIMADO de una capa
// de texto -- combina las piezas de arriba (tamaño de fuente ajustado +
// líneas + line-height propio o heredado del text_style + caja opcional) en
// el MISMO orden que pinta `TextLayer.tsx`, para que
// `flyer-renderer/scripts/metrics-vectors.mjs` (y transitivamente
// `pipeline/layout_gate.py`, vía su espejo Python
// `pipeline/flyer_metrics.py::estimate_text_layer_metrics`) comparen contra
// este único cálculo en vez de reimplementarlo cada uno por su lado.
//
// AV-1: si `box` está presente (incluso con `paddingYEm: 0`), el alto crece
// 2×`paddingYEm`×`fontSize final` -- ver el fix de `TextLayer.tsx` (el
// wrapper de `box` ahora fija su propio `fontSize` para que su padding en
// `em` sea relativo al texto real, no al default de 16px del navegador; sin
// ese fix esta fórmula no podía ser cierta).
export interface TextLayerMetricsInput {
  content: string;
  style: TextStyleName;
  maxWidth: number; // fracción 0-1 del ancho del lienzo (TextLayer.max_width)
  canvasWidth: number;
  sizeFactor?: number;
  letterSpacingEm?: number;
  uppercase?: boolean;
  lineHeight?: number | null; // override de capa; ausente => el del text_style
  box?: { paddingYEm: number } | null;
}

export interface TextLayerMetrics {
  fontSize: number;
  lines: number;
  height: number;
}

export const estimateTextLayerMetrics = ({
  content,
  style,
  maxWidth,
  canvasWidth,
  sizeFactor = 1.0,
  letterSpacingEm = 0,
  uppercase = false,
  lineHeight,
  box,
}: TextLayerMetricsInput): TextLayerMetrics => {
  const styleDef = textStyles[style];
  const maxWidthPx = maxWidth * canvasWidth;
  // IM-1: size_factor multiplica el tamaño base ANTES de fitFontSize, mismo
  // único punto que TextLayer.tsx (applySizeFactor).
  const base = applySizeFactor(
    scaledFontSize(styleDef.fontSize, canvasWidth),
    sizeFactor,
  );
  const fontSize = fitFontSize(
    content,
    base,
    maxWidthPx,
    style,
    letterSpacingEm,
    uppercase,
  );
  const lines = estimateLines(
    content,
    fontSize,
    maxWidthPx,
    letterSpacingEm,
    uppercase,
  );
  const textHeight = lines * (lineHeight ?? styleDef.lineHeight) * fontSize;
  const boxHeight = box ? 2 * box.paddingYEm * fontSize : 0;
  return { fontSize, lines, height: textHeight + boxHeight };
};
